import cn from 'classnames';
import Image, { StaticImageData } from 'next/image';
import React from 'react';
import { useIntl } from 'react-intl';

import FastCompanyLogo from '@/assets/about/outdoorsy-group/fast-company-logo.png';
import timeline1 from '@/assets/about/outdoorsy-group/timeline-1.jpg';
import timeline2 from '@/assets/about/outdoorsy-group/timeline-2.jpg';
import timeline3 from '@/assets/about/outdoorsy-group/timeline-3.jpg';
import timeline4 from '@/assets/about/outdoorsy-group/timeline-4.jpg';
import timeline5 from '@/assets/about/outdoorsy-group/timeline-5.jpg';
import timeline6 from '@/assets/about/outdoorsy-group/timeline-6.jpg';
import timeline7 from '@/assets/about/outdoorsy-group/timeline-7.jpg';
import timeline8 from '@/assets/about/outdoorsy-group/timeline-8.jpg';
import timeline9 from '@/assets/about/outdoorsy-group/timeline-9.jpg';
import timeline10 from '@/assets/about/outdoorsy-group/timeline-10.jpg';
import timeline11 from '@/assets/about/outdoorsy-group/timeline-11.jpg';
import timeline12 from '@/assets/about/outdoorsy-group/timeline-12.jpg';
import timeline13 from '@/assets/about/outdoorsy-group/timeline-13.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';

enum EImageAlignment {
  Sides,
  SidesReversed,
  SidesVerticallyCentered,
  CenterSingle,
  CenterMultiple,
}

enum ETextAlignment {
  Left,
  Right,
}

interface IMobileTimelineItem {
  yearText: string;
  itemText: string;
  imgUrl?: StaticImageData;
  imgClassName?: string;
  img2Url?: StaticImageData;
  img2ClassName?: string;
  textAlignment?: ETextAlignment;
  imageAlignment?: EImageAlignment;
  displayLogo?: boolean;
}

export const MobileTimeline = () => {
  const intl = useIntl();

  return (
    <div className="flex flex-col overflow-hidden gap-y-6">
      <MobileTimelineItem
        yearText="2014"
        itemText={intl.formatMessage({
          defaultMessage: 'Founded and incubated at NFX',
          id: 'XMPCsd',
        })}
        imgUrl={timeline1}
        imgClassName="w-[260px] h-[270px] -mb-[25px] z-10"
        img2Url={timeline2}
        img2ClassName="w-[260px] h-[210px]"
        imageAlignment={EImageAlignment.SidesReversed}
      />

      <MobileTimelineItem
        yearText="2015"
        itemText={intl.formatMessage({ defaultMessage: 'Launch of outdoorsy.com', id: 'iwAJiT' })}
        textAlignment={ETextAlignment.Right}
      />

      <MobileTimelineItem
        yearText="2016"
        itemText={intl.formatMessage({ defaultMessage: 'Launch of Roamly', id: 'naDx8D' })}
        imgUrl={timeline3}
        imgClassName="w-[260px] h-[320px] -mt-[370px] mb-[50px] -ml-[20px]"
        img2Url={timeline4}
        img2ClassName="w-[260px] h-[420px] z-10"
        imageAlignment={EImageAlignment.SidesVerticallyCentered}
      />

      <MobileTimelineItem
        yearText="2021"
        itemText={intl.formatMessage({ defaultMessage: '$120M Funding Round', id: 'prxEQm' })}
      />

      <MobileTimelineItem
        yearText="2021"
        itemText={intl.formatMessage({ defaultMessage: '$1b in sales', id: 'i34fud' })}
        imgUrl={timeline5}
        imgClassName="w-[260px] h-[270px]"
        textAlignment={ETextAlignment.Right}
        imageAlignment={EImageAlignment.CenterSingle}
      />

      <MobileTimelineItem
        yearText="2022"
        itemText={intl.formatMessage({
          defaultMessage:
            'Established Outdoorsy Destination Network purchasing 3 world-class properties in CA, CO, TX',
          id: 'V1qHIv',
        })}
        textAlignment={ETextAlignment.Right}
      />

      <MobileTimelineItem
        yearText="2022"
        itemText={intl.formatMessage({
          defaultMessage: 'Builds first Glamping Retreat',
          id: 'v6qES+',
        })}
        imgUrl={timeline6}
        imgClassName="w-[260px] h-[280px]"
        img2Url={timeline7}
        img2ClassName="w-[260px] h-[270px] -mt-[50px]"
        imageAlignment={EImageAlignment.CenterMultiple}
      />

      <MobileTimelineItem
        yearText="2022"
        itemText={intl.formatMessage({ defaultMessage: '$2bn in sales', id: 'aCxHGd' })}
        imgUrl={timeline8}
        imgClassName="w-[260px] h-[210px]"
        imageAlignment={EImageAlignment.CenterSingle}
      />

      <MobileTimelineItem
        yearText="2023"
        itemText={intl.formatMessage({
          defaultMessage:
            "CEO Jeff Cavins recognized as one of the Most Exceptional Entrepreneurs of 2022 by Goldman Sachs'",
          id: 'JaKAlR',
        })}
        textAlignment={ETextAlignment.Right}
        displayLogo
      />

      <MobileTimelineItem
        yearText="2023"
        itemText={intl.formatMessage({
          defaultMessage:
            "Outdoorsy named to Fast Company's prestigious World's Most Innovative Companies list in the Travel and Hospitality sector",
          id: '6Hi+XV',
        })}
        imgUrl={timeline9}
        imgClassName="w-[260px] h-[320px] -mt-[370px] mb-[50px] -ml-[20px]"
        img2Url={timeline10}
        img2ClassName="w-[260px] h-[420px] z-10"
        imageAlignment={EImageAlignment.SidesVerticallyCentered}
      />

      <MobileTimelineItem
        yearText="2024"
        itemText={intl.formatMessage({
          defaultMessage:
            '1,000,000th family departs on an Outdoorsy trip (7+ million nights outdoors)',
          id: 'l1aySk',
        })}
        textAlignment={ETextAlignment.Right}
      />

      <MobileTimelineItem
        yearText="2024"
        itemText={intl.formatMessage({
          defaultMessage: 'Outdoorsy Texas Hill Country opens',
          id: 'eC/tUT',
        })}
        imgUrl={timeline11}
        imgClassName="w-[260px] h-[270px] z-10"
        img2Url={timeline12}
        img2ClassName="w-[260px] h-[280px] -mt-[150px]"
        textAlignment={ETextAlignment.Right}
        imageAlignment={EImageAlignment.Sides}
      />

      <MobileTimelineItem
        yearText="2024"
        itemText={intl.formatMessage({
          defaultMessage:
            "Jen Young named to 'Inc.'s 2024 Female Founders 250 List' for achieving 175 percent multiyear growth",
          id: 'EhyUBW',
        })}
      />

      <MobileTimelineItem
        yearText="2024"
        itemText={intl.formatMessage({ defaultMessage: '$3b in sales', id: 'QQLDVK' })}
        textAlignment={ETextAlignment.Right}
      />

      <MobileTimelineItem
        yearText="2025"
        itemText={intl.formatMessage({
          defaultMessage:
            'Appointed a Lloyd’s Coverholder, Roamly’s milestone signifies delegated underwriting authority from Lloyd’s of London, reinforcing our deep expertise in underwriting a capability crucial for mobility solutions like carsharing.',
          id: 'tryrnV',
        })}
        imgUrl={timeline13}
        imgClassName="w-[260px] h-[210px]"
        imageAlignment={EImageAlignment.CenterSingle}
      />
    </div>
  );
};

const MobileTimelineItem: React.FC<IMobileTimelineItem> = ({
  yearText,
  itemText,
  imgUrl,
  imgClassName,
  img2Url,
  img2ClassName,
  textAlignment = ETextAlignment.Left,
  imageAlignment,
  displayLogo = false,
}) => {
  // const isCenterAligned = imageAlignment === EImageAlignment.Center;

  return (
    <>
      <MaxWidth
        className={cn('justify-center px-6 mx-auto', {
          'text-right': textAlignment === ETextAlignment.Right,
        })}>
        <div className="italic font-medium text-750">{yearText}</div>
        <div className="text-750">{itemText}</div>
      </MaxWidth>

      {imgUrl && (
        <div
          className={cn(`flex w-full`, {
            'flex-col':
              imageAlignment === EImageAlignment.Sides ||
              imageAlignment === EImageAlignment.SidesReversed,
            'flex-col-reverse': imageAlignment === EImageAlignment.SidesVerticallyCentered,
            'justify-center': imageAlignment === EImageAlignment.CenterSingle,
            'flex-col px-6': imageAlignment === EImageAlignment.CenterMultiple,
          })}>
          <div
            className={cn(`shrink-0 relative ${imgClassName}`, {
              'self-start':
                imageAlignment === EImageAlignment.Sides ||
                imageAlignment === EImageAlignment.SidesVerticallyCentered ||
                imageAlignment === EImageAlignment.CenterMultiple,
              'self-end': imageAlignment === EImageAlignment.SidesReversed,
            })}>
            <Image
              className={cn('rounded-[30px]', {
                'rounded-l-none':
                  imageAlignment === EImageAlignment.Sides ||
                  imageAlignment === EImageAlignment.SidesVerticallyCentered,
                'rounded-r-none': imageAlignment === EImageAlignment.SidesReversed,
              })}
              src={imgUrl}
              alt={`${yearText} - ${itemText}`}
              role="presentation"
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              loading="lazy"
            />
          </div>

          {img2Url && (
            <div
              className={cn(`shrink-0 relative ${img2ClassName}`, {
                'self-end':
                  imageAlignment === EImageAlignment.Sides ||
                  imageAlignment === EImageAlignment.SidesVerticallyCentered ||
                  imageAlignment === EImageAlignment.CenterMultiple,
              })}>
              <Image
                className={cn('rounded-[30px]', {
                  'rounded-r-none':
                    imageAlignment === EImageAlignment.Sides ||
                    imageAlignment === EImageAlignment.SidesVerticallyCentered,
                  'rounded-l-none': imageAlignment === EImageAlignment.SidesReversed,
                })}
                src={img2Url}
                alt={`${yearText} - ${itemText}`}
                role="presentation"
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                loading="lazy"
              />
            </div>
          )}
        </div>
      )}

      {displayLogo && (
        <div className="self-center">
          <div className="flex items-center justify-center rounded-[30px] w-[290px] h-[240px] bg-[#F1F2F3]">
            <Image
              src={FastCompanyLogo.src}
              alt="Fast Company"
              role="presentation"
              width={0}
              height={0}
              layout="responsive"
              objectFit="cover"
              loading="lazy"
            />
          </div>
        </div>
      )}
    </>
  );
};
